import store from "../../../../../store";
import { BrandsEndPointLogic } from "../../../../../logic/Partners/DataIntegrations/BrandsEndPointLogic";
import {
  setBrandDataInModal,
  setIsRunProcessSupportDataLoading,
  setModalOpen,
} from "../../../../../features/brandConfigurationReportSlice";
import {
  RUN_PROCESS_FETCHER_CONFIG_QUERY_ID,
  RUN_PROCESS_INDUSTRY_QUERY_ID,
} from "../constants";

let inFlight: Promise<any> | null = null;

// The Run Process wizard (reused from the legacy module) needs two site-wide
// datasets — fetcherConfigData / brandIndustryData in brandConfigurationReportSlice
// — for its "By Flow" mode. These used to be fetched eagerly on every table
// mount (All Brands / Needs Configuration / Not Connected), which fired both
// queries on page load AND again on every tab switch, even for users who
// never open the wizard. Call this instead, right before opening the modal —
// it fetches once (checked against the slice, guarded against concurrent
// calls) and is a no-op afterwards. While fetching,
// isRunProcessSupportDataLoading is true so the modal can show a skeleton.
export function ensureRunProcessSupportData(oauthTokenHeader: any) {
  const { fetcherConfigData, brandIndustryData } = store.getState().brandConfigurationReport;
  const alreadyFetched =
    Boolean(fetcherConfigData?.query_output) && Boolean(brandIndustryData?.query_output);
  if (alreadyFetched) return Promise.resolve();
  if (inFlight) return inFlight;

  store.dispatch(setIsRunProcessSupportDataLoading(true));
  inFlight = Promise.all([
    BrandsEndPointLogic.getAllConfigForFetcher(
      oauthTokenHeader,
      RUN_PROCESS_FETCHER_CONFIG_QUERY_ID,
      "test",
    ),
    BrandsEndPointLogic.getBrandsForIndustry(
      oauthTokenHeader,
      RUN_PROCESS_INDUSTRY_QUERY_ID,
      "test",
    ),
  ]).finally(() => {
    inFlight = null;
    store.dispatch(setIsRunProcessSupportDataLoading(false));
  });
  return inFlight;
}

// Builds a brand's "By Report" options (row_output_table_data) from the
// fetcherConfigData (RUN_PROCESS_FETCHER_CONFIG_QUERY_ID) response — same
// shape/dedupe as BrandConfigurationOverview.
export function getRowOutputTableData(brandId: any) {
  const { fetcherConfigData } = store.getState().brandConfigurationReport;
  return Array.from(
    new Map(
      (fetcherConfigData?.query_output || [])
        .filter((item: any) => String(item.brand_id) === String(brandId))
        .map((item: any) => [
          item.raw_output_table_name,
          {
            config_name: item.config_name,
            table_id: item.raw_output_table_name,
          },
        ]),
    ).values(),
  );
}

const withRowOutputTableData = (brands: any[]) =>
  brands.map((brand: any) => ({
    ...brand,
    row_output_table_data: getRowOutputTableData(brand.brandId),
  }));

// Opens the Run Process modal right away (it shows a skeleton while the
// support data loads), then fills each brand's "By Report" options once the
// fetcher config query has resolved.
export async function openRunBrandModal(
  oauthTokenHeader: any,
  brands: { brandId: any; brandName: any }[],
) {
  store.dispatch(
    setModalOpen({ isOpen: true, data: withRowOutputTableData(brands) }),
  );
  await ensureRunProcessSupportData(oauthTokenHeader);
  // Modal may have been closed (or reopened for other brands) meanwhile.
  const { isModalOpen, brandDataInModal } =
    store.getState().brandConfigurationReport;
  if (!isModalOpen) return;
  store.dispatch(
    setBrandDataInModal(withRowOutputTableData(brandDataInModal || [])),
  );
}
